import { ClientState } from "../shell"
import { arrayMax } from "../utils/array"
import { HoldingEvent, HoldingEvents, HoldingEventUser, HoldingSession, OmniaApiChange } from "./types"
import { getConflictId, setupHoldingEvents, setupHoldingSessions, setupInitialHoldingEvents } from "./utils"

export class OmniaApiState {

    static $inject = ["$rootScope", "$timeout"]

    constructor(private readonly $rootScope: ng.IRootScopeService, private readonly $timeout: ng.ITimeoutService) {
    }

    public currentSessionId: string

    private _changeNumber = 0
    private _changes: OmniaApiChange[] = []

    private _lastEventDate: Date

    /**
     * Latest client state for the curent session
     */
    private _clientState: ClientState

    /**
     * ConflictId for the current session
     */
    private _confictId: string

    /**
     * List of sessions conflicting with the current one, based on the client state
     */
    private _conflicts: HoldingSession[]


    getVersion() {

        return this._changeNumber
    }


    incrementVersion() {

        this.recordChange({ local: true })

        return this._changeNumber

    }


    recordChange(change: Omit<OmniaApiChange, "id">) {

        this.recordChanges([change])

    }


    private recordChanges(changes: Omit<OmniaApiChange, "id">[]) {

        changes.forEach(c => this._changes.push({
            id: ++this._changeNumber,
            ...c
        }))

    }

    getChanges(fromId: number, filter?: (change: OmniaApiChange) => boolean) {

        filter = filter || (s => true)

        return this._changes.filter(c => (c.id > fromId) && filter(c))

    }


    getChangesFromSource(fromId: number, sourceFilter?: (source: OmniaApiChange["source"]) => boolean) {

        sourceFilter = sourceFilter || (s => true)

        return this._changes.filter(c => (c.id > fromId) && sourceFilter(c.source))

    }

    private recordChangesFromExternalEvents(events: HoldingEvent[]) {

        const changes = events.map(e => {

            const change: Omit<OmniaApiChange, "id"> = {
                local: false,
                action: e.Action,
                data: e.Data,
                source: e.Source,
                type: e.ObjectType,
            }

            return change

        })

        this.recordChanges(changes)

    }


    isApiRefreshPending() {
        return !!this._pendingEvents
    }

    private _sessions = setupHoldingSessions([], "")

    get sessions() {
        return this._sessions
    }


    public updateSessions(sessions: HoldingSession[]) {
        sessions = [...sessions || []]
        this._sessions = setupHoldingSessions(sessions, this.currentSessionId)
        this._conflicts = findConflicts(this._sessions.active, this._confictId)
        this.$rootScope.$broadcast("omnia.sessionsChanged", this._sessions.active)
        this.$rootScope.$applyAsync()
    }

    public setClientState(state: ClientState) {
        this._clientState = state
        this._confictId = state ? getConflictId(state) : undefined
        this._conflicts = findConflicts(this._sessions.active, this._confictId)
    }

    get conflicts() {
        return this._conflicts
    }

    get events() {
        return this._events
    }

    get lastEventDate() {
        return this._lastEventDate
    }

    private _events: HoldingEvents = {
        events: [],
        unread: []
    }

    public setupEvents(events: HoldingEvent[], users: HoldingEventUser[]) {

        const e = setupInitialHoldingEvents(events, users)

        this._events = {
            events: e,
            unread: []
        }

        this.$rootScope.$applyAsync()

    }

    private _pendingEvents: {
        events: HoldingEvent[]
        debounce: ng.IPromise<any>
    }

    public mergeEvents(events: HoldingEvent[], debounce = 500) {

        const getUniqueEvents = (current: HoldingEvent[], added: HoldingEvent[]) => {

            const set = new Set(current.map(c => c.Id))

            added = added.filter(a => !set.has(a.Id)).map(a => ({ ...a, Unread: !a.CurrentSession }))

            const unique = current.concat(added)

            const ordered = unique.sort((a, b) => (b.Date.valueOf() - a.Date.valueOf()) || (b.Id - a.Id))

            return [ordered, added]

        }

        const merge = (newEvents: HoldingEvent[]) => {

            newEvents = setupHoldingEvents([...newEvents || []], this._sessions)

            const [events, added] = getUniqueEvents(this._events.events, newEvents)

            const active = events.filter(e => e.Unread)

            this._events = {
                events,
                unread: active
            }

            this._lastEventDate = arrayMax(events, e => e.Date)

            const otherSessions = added.filter(a => !a.CurrentSession)

            if (otherSessions.length) {

                this.recordChangesFromExternalEvents(otherSessions)

                this.$rootScope.$broadcast("omnia.refresh", added)

            }

            this.$rootScope.$applyAsync()

        }

        const pending = this._pendingEvents

        if ((debounce <= 0) && (!pending)) {
            merge(events)
            return
        }

        this._pendingEvents = pending
            ? {
                ...pending,
                events: pending.events.concat(events)
            }
            : {
                events,
                debounce: this.$timeout(debounce).then(() => {
                    merge(this._pendingEvents.events)
                    this._pendingEvents = undefined
                })
            }
    }


    updateMasterData(update: { year: number; datasets: string[] }) {

        this.recordChange({
            source: "shared",
            local: false,
            data: update
        })

        this.$rootScope.$broadcast("omnia.refresh")

    }

}

function findConflicts(sessions: HoldingSession[], conflictId: string) {

    return conflictId
        ? sessions.filter(s => (!s.CurrentSession) && (s.ConflictId == conflictId))
        : []

}
